import { useEffect, useMemo, useState } from "react";
import ModalWrapper from "./ModalWrapper";
import { NumberInput, SelectInput, TextInput } from "../Inputs";
import constants from "../../utils/constants";
import { formatCurrency } from "../../utils/helpers";
import { CheckIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { PrimaryButton } from "../Buttons";

/**
 * SPR-014: refund confirmation.
 *
 * Previously this asked for a pennies amount with NO preview and no upper bound — the operator had no idea what was
 * actually paid or how much was still refundable. It now takes a `preview` (RefundProductDetails) and:
 *   - shows PaidPrice / EligibleRefundAmount / any server warnings BEFORE confirming,
 *   - defaults the amount to the eligible maximum and rejects anything above it,
 *   - for a PARTIAL amount, offers "cancel the membership" vs "keep it active".
 *
 * The keep-active choice is hidden for a full refund on purpose: the server ignores it there (a full refund always
 * ends the subscription), so offering it would promise something that will not happen.
 */
function RefundConfirmationModal({
    modalOpen,
    title,
    message,
    textToMatch,
    confirmButtonText,
    setModalOpen,
    onConfirm,
    productPrice,
    preview,          // RefundProductDetails | null — null while loading or if the preview failed
    previewLoading,
  }) {
    const [userInput, setUserInput] = useState("");
    const [refundAmount, setRefundAmount] = useState();
    const [refundReason, setRefundReason] = useState();
    const [keepMembershipActive, setKeepMembershipActive] = useState(false);

    const eligible = preview?.eligibleRefundAmount;
    const paid = preview?.paidPrice ?? productPrice;

    // Default the amount to the full eligible refund once the preview lands — the common case, and it stops the
    // operator having to work the number out. Only seeds while untouched, so it never clobbers typing.
    useEffect(() => {
      if (eligible != null && refundAmount === undefined) setRefundAmount(eligible);
    }, [eligible, refundAmount]);

    // Reset per-open so a previous refund's amount/choice can't leak into the next one.
    useEffect(() => {
      if (!modalOpen) {
        setUserInput("");
        setRefundAmount(undefined);
        setRefundReason(undefined);
        setKeepMembershipActive(false);
      }
    }, [modalOpen]);

    const userInputMatches = useMemo(
      () => textToMatch === userInput,
      [textToMatch, userInput]
    );

    const exceedsEligible = eligible != null && refundAmount != null && refundAmount > eligible;
    const amountIsPositive = refundAmount != null && refundAmount > 0;
    // Partial == strictly less than everything that was PAID. Mirrors the server, which decides on the amount
    // actually refunded rather than on any flag, so the UI cannot offer a choice the server will ignore.
    const isPartial = amountIsPositive && paid != null && refundAmount < paid;
    const canConfirm = userInputMatches && amountIsPositive && !exceedsEligible && !previewLoading;

    return (
      <ModalWrapper open={modalOpen} setOpen={setModalOpen}>
        <div className="w-[85vw] lg:w-[35vw]">
          <div className="pb-4 text-center flex items-center flex-col">
            {title}
          </div>
          {message && (
            <div className="pb-6 mx-6 text-center text-sm text-gray-500">
              {message}
            </div>
          )}

          {/* SPR-014: what the server says is actually refundable, shown before anything is committed. */}
          <div className="mb-6 rounded-md bg-gray-50 p-3 text-sm">
            {previewLoading ? (
              <div className="text-gray-500">Checking what can be refunded…</div>
            ) : preview ? (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount paid</span>
                  <span className="font-semibold">{formatCurrency(paid ?? 0, true)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Eligible for refund</span>
                  <span className="font-semibold">{formatCurrency(eligible ?? 0, true)}</span>
                </div>
                {preview.ownedProductName && (
                  <div className="mt-1 text-xs text-gray-500">{preview.ownedProductName}</div>
                )}
                {preview.warnings?.length > 0 && (
                  <ul className="mt-2 list-disc pl-5 text-xs text-yellow-800">
                    {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                )}
              </>
            ) : (
              <div className="text-yellow-800">
                Could not load refund details. Check the amount carefully before confirming.
              </div>
            )}
          </div>

          <div className="mb-6">
            <SelectInput
                label={"Refund reason (optional)"}
                placeholder={constants.REFUND_REASONS[0].label}
                options={constants.REFUND_REASONS}
                value={refundReason}
                onChange={(reason) => {setRefundReason(reason)} }
                onClear={() => {setRefundReason(undefined)}}
            />
          </div>

          <div className="mb-2">
            <NumberInput
              placeholder={eligible ?? productPrice}
              label={"Refund amount (pennies)"}
              value={refundAmount ?? ""}
              onChange={(e) => setRefundAmount(isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber)}
              iconColor={exceedsEligible ? "notification" : "success"}
              Icon={exceedsEligible ? ExclamationTriangleIcon : CheckIcon}
              isCurrency
              isPennies
            />
          </div>
          {exceedsEligible && (
            <div className="mb-4 text-xs text-red-600">
              That is more than the {formatCurrency(eligible, true)} eligible for refund.
            </div>
          )}

          {/* SPR-014: partial refunds only — a full refund always ends the subscription server-side. */}
          {isPartial && !exceedsEligible && (
            <div className="mb-6 mt-4 rounded-md border border-gray-200 p-3">
              <div className="mb-2 text-sm font-semibold text-gray-900">
                This is a partial refund. What should happen to the membership?
              </div>
              <label className="flex items-start gap-x-2 py-1 text-sm cursor-pointer">
                <input
                  type="radio"
                  className="mt-1"
                  name="keepMembershipActive"
                  checked={!keepMembershipActive}
                  onChange={() => setKeepMembershipActive(false)}
                />
                <span>
                  <span className="font-medium">Cancel the membership</span>
                  <span className="block text-xs text-gray-500">
                    The subscription ends immediately. This is the existing behaviour.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-x-2 py-1 text-sm cursor-pointer">
                <input
                  type="radio"
                  className="mt-1"
                  name="keepMembershipActive"
                  checked={keepMembershipActive}
                  onChange={() => setKeepMembershipActive(true)}
                />
                <span>
                  <span className="font-medium">Keep the membership active</span>
                  <span className="block text-xs text-gray-500">
                    The subscription keeps its current end date and <span className="font-semibold">will still
                    renew</span> at its normal price.
                  </span>
                </span>
              </label>
            </div>
          )}

          <div className="mb-6">
            <TextInput
              label={"Enter username to confirm"}
              placeholder={textToMatch}
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              iconColor={userInputMatches ? "success" : "notification"}
              Icon={userInputMatches ? CheckIcon : ExclamationTriangleIcon}
            />
          </div>

          <div className="mt-3">
            <PrimaryButton
              text={confirmButtonText}
              onClick={() => {onConfirm(refundAmount, refundReason?.value, isPartial ? keepMembershipActive : false)}}
              disabled={!canConfirm}
            />
          </div>
        </div>
      </ModalWrapper>
    );
}

export default RefundConfirmationModal;
